import React from 'react';
import { Avatar, Box, Button, Card, CardContent, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';

import COLORS from 'theme/colors';
import { providerPhotoUrl } from 'utils/providerPhoto';
import { formatProviderName, formatSlotLocation } from './mappers';
import { AvailabilitySlot, Provider } from './types';

const VISIBLE_SLOT_COUNT = 3;
const SCHEDULING_PHONE_NUMBER = '(618) 310-0085';

interface ProviderCardProps {
  provider: Provider;
  onSelectSlot: (provider: Provider, slot: AvailabilitySlot) => void;
  onMore: (provider: Provider) => void;
}

const ProviderCard = ({ provider, onSelectSlot, onMore }: ProviderCardProps) => {
  const visibleSlots = provider.availability.slice(0, VISIBLE_SLOT_COUNT);
  const hasMore = provider.hasMoreAvailability;
  // No slot to show at all (not just none of the first 3 - hasMore would still offer a way in
  // otherwise) - the 3-slot row has nothing to render, so a call-to-schedule line takes its
  // place instead of leaving the card's Availability section empty.
  const hasNoAvailability = visibleSlots.length === 0 && !hasMore;

  return (
    <Card
      variant='outlined'
      sx={{
        borderRadius: '12px',
        border: `1px solid ${COLORS.Gray300}`,
        boxShadow: 'none',
        height: '100%',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            src={providerPhotoUrl(provider.id)}
            alt={provider.name}
            sx={{ width: 120, height: 120, backgroundColor: COLORS.LightBlue, color: COLORS.DarkBlue }}
          >
            <PersonIcon sx={{ fontSize: 56 }} />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant='h4' sx={{ fontWeight: 600, color: COLORS.Gray900, wordBreak: 'break-word' }}>
              {formatProviderName(provider)}
            </Typography>
            <Typography variant='body2' sx={{ color: COLORS.Gray600 }}>
              {provider.description}
            </Typography>
          </Box>
        </Box>

        <Typography variant='body2' sx={{ color: COLORS.Gray900, fontWeight: 600, mb: 1 }}>
          Availability
        </Typography>
        {hasNoAvailability ? (
          <Typography variant='body2' sx={{ color: COLORS.DarkBlue, fontWeight: 600 }}>
            CALL TO SCHEDULE: {SCHEDULING_PHONE_NUMBER}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {visibleSlots.map((slot) => (
              <Button
                key={slot.id}
                variant='contained'
                title={formatSlotLocation(slot)}
                onClick={() => onSelectSlot(provider, slot)}
                sx={{
                  textTransform: 'none',
                  width: '100%',
                  minWidth: 0,
                  minHeight: 'auto',
                  px: 0.75,
                  py: 0.5,
                  fontSize: 11,
                  lineHeight: 1.3,
                  whiteSpace: 'normal',
                  textAlign: 'center',
                }}
              >
                {slot.day} &middot; {slot.time}
              </Button>
            ))}
            {hasMore ? (
              <Button
                variant='text'
                onClick={() => onMore(provider)}
                sx={{ textTransform: 'none', color: COLORS.Link, width: '100%', px: 0.75 }}
              >
                More
              </Button>
            ) : null}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ProviderCard;
